// DealReferralsCard — the referral picture for one customer, on their deal.
//
// Two things, both on the Overview tab:
//   • "Referred by …" credit when THIS deal came from a referral, so anyone
//     opening the job can see who's owed $250 when it builds.
//   • The referrals this customer has GIVEN us, with where each one stands.
//
// Renders nothing when the customer has neither — no empty box on the 5,000
// deals that have nothing to do with the program.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import DealLink from "../DealLink";
import {
  type Referral, REFERRAL_FEE, STATUS_LABELS, STATUS_CLASSES,
  PAYOUT_LABELS, PAYOUT_CLASSES, fetchReferralsForDeal, money,
} from "../../lib/referrals";

export default function DealReferralsCard({ dealId }: { dealId: string }) {
  const [given, setGiven] = useState<Referral[]>([]);
  const [gotFrom, setGotFrom] = useState<Referral | null>(null);
  const [inboundCount, setInboundCount] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [mine, inbound] = await Promise.all([
          fetchReferralsForDeal(dealId),
          // NOT .maybeSingle(): nothing stops two customers from referring the
          // same person, and maybeSingle() ERRORS on >1 row rather than
          // returning null -- which would blank the whole card. Take the
          // earliest (first to refer them) and flag the collision.
          supabase.from("referrals")
            .select("id, referrer_name, referrer_deal_id, payout_status, payout_amount, payout_date")
            .eq("referred_deal_id", dealId)
            .order("created_at", { ascending: true }),
        ]);
        if (cancelled) return;
        setGiven(mine);
        const inboundRows = (inbound.data ?? []) as unknown as Referral[];
        setGotFrom(inboundRows[0] ?? null);
        setInboundCount(inboundRows.length);
      } catch {
        /* card is additive — a failure just hides it */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [dealId]);

  if (!loaded || (given.length === 0 && !gotFrom)) return null;

  const earnedByThisCustomer = given
    .filter((r) => ["earned", "approved", "paid"].includes(r.payout_status))
    .reduce((s, r) => s + Number(r.payout_amount ?? 0), 0);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-bardin-ink text-sm">🤝 Referrals</h3>
        <Link to="/referrals" className="text-xs text-bardin-teal font-semibold underline">
          Referrals board →
        </Link>
      </div>

      {gotFrom && (
        <div className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-2">
          <p className="text-sm text-orange-900">
            <span className="font-semibold">Referred by {gotFrom.referrer_name}</span>
            {gotFrom.referrer_deal_id && (
              <> · <DealLink id={gotFrom.referrer_deal_id} className="underline font-medium">
                open their job
              </DealLink></>
            )}
          </p>
          <p className="text-xs text-orange-800 mt-0.5">
            {gotFrom.payout_status === "paid"
              ? `${money(gotFrom.payout_amount ?? REFERRAL_FEE)} referral fee paid${gotFrom.payout_date ? ` ${gotFrom.payout_date}` : ""}.`
              : gotFrom.payout_status === "pending"
                ? `${money(gotFrom.payout_amount ?? REFERRAL_FEE)} becomes owed to them once this roof is built.`
                : `${money(gotFrom.payout_amount ?? REFERRAL_FEE)} — ${PAYOUT_LABELS[gotFrom.payout_status]}.`}
          </p>
        </div>
      )}

      {inboundCount > 1 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
          <span className="font-semibold">⚠ {inboundCount} customers referred this homeowner.</span>{" "}
          Each one is tracked separately and would each be owed a fee — worth a look before approving.
        </div>
      )}

      {given.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-600 mb-1.5">
            This customer has referred {given.length} {given.length === 1 ? "person" : "people"}
            {earnedByThisCustomer > 0 && (
              <span className="text-emerald-700"> · {money(earnedByThisCustomer)} earned</span>
            )}
          </p>
          <div className="space-y-1.5">
            {given.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 text-sm border border-slate-200 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <span className="font-medium text-bardin-ink">{r.referred_name}</span>
                  {r.referred_deal_id && (
                    <> · <DealLink id={r.referred_deal_id} className="text-bardin-teal underline text-xs">job</DealLink></>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${STATUS_CLASSES[r.status]}`}>
                    {STATUS_LABELS[r.status]}
                  </span>
                  {r.payout_status !== "pending" && (
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${PAYOUT_CLASSES[r.payout_status]}`}>
                      {r.payout_status === "paid" ? money(r.payout_amount ?? REFERRAL_FEE) : PAYOUT_LABELS[r.payout_status]}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
